import Cookies from 'js-cookie';

/**
 * CookieHelper provides a simple interface for interacting with browser cookies.
 * It wraps the js-cookie library to provide a consistent and easy-to-use API.
 */
class CookieHelper {
  /**
   * Set a cookie value.
   * @param name The name of the cookie.
   * @param value The value to store. If an object is provided, it will be stringified.
   * @param options Optional cookie attributes (expires, path, domain, secure, sameSite).
   */
  static set(name: string, value: any, options?: Cookies.CookieAttributes): void {
    const stringValue = typeof value === 'object' ? JSON.stringify(value) : String(value);
    Cookies.set(name, stringValue, { 
      expires: 7, // Default to 7 days if not specified
      path: '/',
      secure: window.location.protocol === 'https:',
      sameSite: 'Lax',
      ...options 
    });
  }

  /**
   * Get a cookie value.
   * @param name The name of the cookie.
   * @returns The cookie value, or undefined if not found.
   */
  static get(name: string): string | undefined {
    return Cookies.get(name);
  }

  /**
   * Get a cookie value and parse it as JSON.
   * @param name The name of the cookie.
   * @returns The parsed object, or null if not found or invalid JSON.
   */
  static getJSON<T>(name: string): T | null {
    const value = Cookies.get(name);
    if (!value) return null;
    try {
      return JSON.parse(value) as T;
    } catch (e) {
      console.error(`Error parsing cookie "${name}":`, e);
      return null;
    }
  }

  /**
   * Remove a cookie.
   * @param name The name of the cookie.
   * @param options Optional cookie attributes (path, domain).
   */
  static remove(name: string, options?: Cookies.CookieAttributes): void {
    Cookies.remove(name, { path: '/', ...options });
  }

  /**
   * Check if a cookie exists.
   * @param name The name of the cookie.
   * @returns True if the cookie exists, false otherwise.
   */
  static exists(name: string): boolean {
    return Cookies.get(name) !== undefined;
  }
}

export default CookieHelper;
